import {
  Delete,
  Download,
  FindInPage,
  MoreVert,
  PictureInPicture,
  Search,
} from '@mui/icons-material';
import {
  Checkbox,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  MenuList,
  TableCell,
  TableRow,
  Tooltip,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { getFetch } from '../hooks/useFetch';
import { IDocument } from '../Interfaces/IDocument';
import { IDocumentDownloaded } from '../Interfaces/IDocumentDownloaded';
import { IMIMEType } from '../Interfaces/IMIMEType';
import { useSidepanelDocumentContext } from '../LegalTransaction/LegalTransactionEdit';
import { DocumentDownloadedDialog } from './DocumentAssistant/DocumentDownloadedDialog';
import { DocumentPreviewDialog } from './DocumentAssistant/DocumentPreviewDialog';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { ILegalClient } from '../Interfaces/ILegalClient';
import { useDocumentTransferActions } from '../LegalTransaction/DocumentTransfer/DocumentTransferActions';
import { IDocumentLocation } from '../LegalTransaction/DocumentTransfer/documentTransferTypes';

interface IProps {
  documentObject: IDocument;
  documentArray: IDocument[];
  setDocumentArray: (newDocuments: IDocument[]) => void;
  mimeTypeArray: IMIMEType[];
  allowRemove: boolean;
  downloadedArray?: IDocumentDownloaded[];
  justButtons?: boolean;
  enableSidepanel?: boolean;
  isStandarddocument?: boolean;
  isSendEmailPossible?: boolean;
  checkedBoxes?: number[];
  setCheckedBoxes?: React.Dispatch<React.SetStateAction<number[]>>;
  assignedLegalClients: ILegalClient[];
  /**
   * Fundstelle des Dokuments im Vorgang. Ist sie gesetzt, koennen Dokumente von
   * hier aus in andere Bereiche verschoben und umbenannt werden.
   */
  documentLocation?: IDocumentLocation;
}

export const base64Download = (
  localDocumentObject: IDocument,
  mimeTypes: IMIMEType[]
) => {
  const currentMimeType = mimeTypes.find(
    x => x.idMIMEType === localDocumentObject.idMIMEType
  );

  if (localDocumentObject.Data && currentMimeType) {
    const byteCharacters = atob(localDocumentObject.Data);
    // Each character's code point (charCode) will be the value of the byte.
    // We can create an array of byte values by applying this using the .charCodeAt method for each character in the string.
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    //You can convert this array of byte values into a real typed byte array by passing it to the Uint8Array constructor.
    const byteArray = new Uint8Array(byteNumbers);
    // This in turn can be converted to a BLOB by wrapping it in an array and passing it to the Blob constructor.
    const blob = new Blob([byteArray], { type: currentMimeType.MIMEType });
    const blobUrl = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = localDocumentObject.FileName;
    a.click();
  }
};

export const DocumentRow: React.FC<IProps> = ({
  documentObject,
  documentArray,
  setDocumentArray,
  mimeTypeArray,
  allowRemove,
  downloadedArray,
  justButtons,
  enableSidepanel = false,
  isStandarddocument = false,
  isSendEmailPossible = true,
  checkedBoxes,
  setCheckedBoxes,
  assignedLegalClients,
  documentLocation,
}) => {
  const { setSelectedSidepanelDocument } = useSidepanelDocumentContext();

  const [localDocumentObject, setLocalDocumentObject] =
    useState(documentObject);
  const [isOpenDownloaded, setIsOpenDownloaded] = useState(false);
  const [isOpenPreview, setisOpenPreview] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const transferActions = useDocumentTransferActions({
    documentObject: localDocumentObject,
    location: documentLocation,
    onOpenDialog: () => setMenuAnchor(null),
  });

  /**
   * Die lokale Kopie dient nur als Zwischenspeicher fuer den nachgeladenen
   * Dateiinhalt (`Data`). Alles andere gehoert dem uebergebenen Array: aendert
   * sich dort z. B. der Dateiname durch Umbenennen, muss die Zeile das
   * uebernehmen — sonst zeigt sie bis zum naechsten Mount den alten Stand.
   */
  useEffect(() => {
    setLocalDocumentObject(previous =>
      previous.FileName === documentObject.FileName &&
      previous.idMIMEType === documentObject.idMIMEType &&
      previous.perEmail === documentObject.perEmail &&
      previous.isFromTemplate === documentObject.isFromTemplate
        ? previous
        : {
            ...documentObject,
            Data: documentObject.Data ?? previous.Data,
          }
    );
  }, [documentObject]);

  const handleDownload = async () => {
    if (localDocumentObject.Data) {
      base64Download(localDocumentObject, mimeTypeArray);
    } else {
      getFetch(
        '/document/',
        localDocumentObject.idDocument,
        (localDocumentObject: IDocument) => {
          setLocalDocumentObject(localDocumentObject);
          base64Download(localDocumentObject, mimeTypeArray);
        }
      );
    }
  };

  const handleOpenPreview = () => {
    if (!localDocumentObject.Data) {
      getFetch(
        '/document/',
        localDocumentObject.idDocument,
        setLocalDocumentObject
      );
    }
    setisOpenPreview(true);
  };

  const handleRemove = () => {
    setDocumentArray([
      ...documentArray.filter(
        document => document.idDocument !== localDocumentObject.idDocument
      ),
    ]);
  };

  const handleOpenSidepanel = () => {
    setSelectedSidepanelDocument(localDocumentObject);

    if (!localDocumentObject.Data) {
      getFetch(
        '/document/',
        localDocumentObject.idDocument,
        (loaded: IDocument) => {
          setLocalDocumentObject(loaded);
          setSelectedSidepanelDocument(loaded);
        }
      );
    }
  };

  /**
   * Immer sichtbar: Bild-in-Bild und Vorschau. Alles Weitere steckt im
   * Drei-Punkte-Menue — einheitlich mit der internen Datenablage.
   */
  const renderVisibleActions = () => (
    <>
      {enableSidepanel && (
        <Tooltip title='Bild-in-Bild öffnen'>
          <IconButton size='small' onClick={handleOpenSidepanel}>
            <PictureInPicture fontSize='small' />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title='Vorschau'>
        <IconButton size='small' onClick={handleOpenPreview}>
          <FindInPage fontSize='small' />
        </IconButton>
      </Tooltip>
    </>
  );

  const renderActionMenu = () => (
    <>
      {/* Die Dialoge stehen bewusst ausserhalb des Menues — sonst wuerden sie
          mit dem Menue unmountet und schloessen sich sofort wieder. */}
      {transferActions.dialogs}
      <Tooltip title='Weitere Aktionen'>
        <IconButton
          size='small'
          onClick={(event: React.MouseEvent<HTMLElement>) => {
            event.stopPropagation();
            setMenuAnchor(event.currentTarget);
          }}
        >
          <MoreVert fontSize='small' />
        </IconButton>
      </Tooltip>
      <Menu
        open={menuAnchor !== null}
        anchorEl={menuAnchor}
        onClose={() => setMenuAnchor(null)}
        onClick={(event: React.MouseEvent) => event.stopPropagation()}
      >
        <MenuList dense>
          <MenuItem
            onClick={() => {
              handleDownload();
              setMenuAnchor(null);
            }}
          >
            <ListItemIcon>
              <Download fontSize='small' />
            </ListItemIcon>
            <ListItemText>Herunterladen</ListItemText>
          </MenuItem>
          {/* Die Zahlen holt sich der Dialog selbst — unabhaengig davon, ob die
              Liste hier durchgereicht wurde. */}
          {localDocumentObject.idDocument > 0 && (
            <MenuItem
              onClick={() => {
                setIsOpenDownloaded(true);
                setMenuAnchor(null);
              }}
            >
              <ListItemIcon>
                <Search fontSize='small' />
              </ListItemIcon>
              <ListItemText>Anzahl Downloads</ListItemText>
            </MenuItem>
          )}
          {transferActions.isAvailable && <Divider />}
          {transferActions.menuItems}
          {(allowRemove || localDocumentObject.idDocument < 0) && <Divider />}
          {(allowRemove || localDocumentObject.idDocument < 0) && (
            <MenuItem
              onClick={() => {
                handleRemove();
                setMenuAnchor(null);
              }}
            >
              <ListItemIcon>
                <Delete fontSize='small' />
              </ListItemIcon>
              <ListItemText>Löschen</ListItemText>
            </MenuItem>
          )}
        </MenuList>
      </Menu>
    </>
  );

  const handlePerEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDocumentArray(
      documentArray.map(document =>
        document.idDocument === documentObject.idDocument
          ? { ...document, perEmail: event.target.checked }
          : document
      )
    );
  };

  if (justButtons) {
    return (
      <>
        <DocumentDownloadedDialog
          open={isOpenDownloaded}
          onClose={() => setIsOpenDownloaded(false)}
          localDocumentObject={localDocumentObject}
          assignedLegalClients={assignedLegalClients}
        />
        <DocumentPreviewDialog
          open={isOpenPreview}
          localDocumentObject={localDocumentObject}
          onClose={() => setisOpenPreview(false)}
        />
        {renderVisibleActions()}
        {renderActionMenu()}
      </>
    );
  } else if (isStandarddocument) {
    return (
      <>
        <DocumentDownloadedDialog
          open={isOpenDownloaded}
          onClose={() => setIsOpenDownloaded(false)}
          localDocumentObject={localDocumentObject}
          assignedLegalClients={assignedLegalClients}
        />
        <DocumentPreviewDialog
          open={isOpenPreview}
          localDocumentObject={localDocumentObject}
          onClose={() => setisOpenPreview(false)}
        />
        <TableRow>
          <TableCell>
            {localDocumentObject.idDocument < 0 ? (
              <>Neu</>
            ) : (
              localDocumentObject.idDocument
            )}
          </TableCell>
          {downloadedArray && (
            <TableCell>
              {
                downloadedArray.filter(
                  document =>
                    document.idDocument === localDocumentObject.idDocument
                ).length
              }
            </TableCell>
          )}
          <TableCell>{localDocumentObject.FileName}</TableCell>
          <TableCell>
            {!!documentObject.isFromTemplate ? <CheckIcon /> : <CloseIcon />}
          </TableCell>
          <TableCell>
            <Checkbox
              checked={!!documentObject.perEmail}
              disabled={!isSendEmailPossible}
              onChange={handlePerEmailChange}
            />
          </TableCell>

          <TableCell>
            {renderVisibleActions()}
            {renderActionMenu()}
          </TableCell>
        </TableRow>
      </>
    );
  } else {
    return (
      <>
        <DocumentDownloadedDialog
          open={isOpenDownloaded}
          onClose={() => setIsOpenDownloaded(false)}
          localDocumentObject={localDocumentObject}
          assignedLegalClients={assignedLegalClients}
        />
        <DocumentPreviewDialog
          open={isOpenPreview}
          localDocumentObject={localDocumentObject}
          onClose={() => setisOpenPreview(false)}
        />
        <TableRow>
          {checkedBoxes && setCheckedBoxes && (
            <TableCell>
              <Checkbox
                checked={
                  checkedBoxes.find(x => x === documentObject.idDocument) !==
                  undefined
                }
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  if (event.target.checked) {
                    setCheckedBoxes(prev => [
                      ...prev,
                      documentObject.idDocument,
                    ]);
                  } else {
                    setCheckedBoxes(prev => [
                      ...prev.filter(x => x !== documentObject.idDocument),
                    ]);
                  }
                }}
              />
            </TableCell>
          )}
          <TableCell>
            {localDocumentObject.idDocument < 0 ? (
              <>Neu</>
            ) : (
              localDocumentObject.idDocument
            )}
          </TableCell>
          {downloadedArray && (
            <TableCell>
              {
                downloadedArray.filter(
                  document =>
                    document.idDocument === localDocumentObject.idDocument
                ).length
              }
            </TableCell>
          )}
          <TableCell>{localDocumentObject.FileName}</TableCell>
          <TableCell>
            {renderVisibleActions()}
            {renderActionMenu()}
          </TableCell>
        </TableRow>
      </>
    );
  }
};
